// Trainee Learning Path — an interactive vertical "journey" roadmap.
// Each pathway is a sequence of milestone nodes connected by a progress spine that
// fills with gold as courses are completed. Nodes show completed / active / locked states.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { motion } from 'framer-motion';
import { Route as RouteIcon, Check, Play, Lock, ChevronRight, Loader2, Trophy, Flag } from 'lucide-react';

const STATUS = {
    completed: { label: 'Completed', cls: 'text-emerald-600' },
    in_progress: { label: 'In progress', cls: 'text-amber-600' },
    not_started: { label: 'Locked — not started', cls: 'text-brand-navy/40' },
};

export default function TraineePathways() {
    const [paths, setPaths] = useState(null);
    useEffect(() => { api.get('/pathways').then((r) => setPaths(r.data || [])).catch(() => setPaths([])); }, []);

    if (!paths) return <div className="py-16 text-center text-brand-navy/40"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

    return (
        <div data-testid="trainee-pathways-page">
            <div className="text-xs uppercase tracking-wider font-bold text-white/70 inline-flex items-center gap-1.5 mb-1.5"><RouteIcon className="h-4 w-4 text-brand-yellow" /> Learning Journey</div>
            <h1 className="text-3xl md:text-4xl font-black tracking-tighter text-white">Your Learning Paths</h1>
            <p className="text-sm text-white/70 font-medium mt-1 mb-7">Follow the trail, complete each milestone, and reach the finish line to master a skill.</p>

            {paths.length === 0 && (
                <div className="bg-white rounded-2xl border border-brand-navy/5 p-10 text-center text-brand-navy/45 shadow-[0_2px_12px_rgba(35,31,32,0.05)]" data-testid="pathways-empty">
                    No learning paths assigned to you yet. Check back soon.
                </div>
            )}

            <div className="space-y-8">
                {paths.map((p) => <Journey key={p.id} p={p} />)}
            </div>
        </div>
    );
}

function Journey({ p }) {
    const items = p.items || [];
    const pct = Number.isFinite(p.progress_percent) ? Math.round(p.progress_percent) : 0;
    const allDone = items.length > 0 && p.completed_count === p.total_count;

    return (
        <div className="bg-white rounded-3xl border border-brand-navy/5 overflow-hidden shadow-[0_2px_16px_rgba(35,31,32,0.06)]" data-testid={`pathway-${p.id}`}>
            {/* Journey header */}
            <div className="relative bg-brand-navy p-5 sm:p-6 overflow-hidden">
                <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: 'radial-gradient(circle at 90% 10%, rgba(0,219,121,0.7), transparent 50%)' }} />
                <div className="relative flex items-center justify-between gap-4">
                    <div className="min-w-0">
                        <div className="font-bold text-xl text-white tracking-tight truncate">{p.name}</div>
                        {p.description && <p className="text-sm text-white/65 mt-1 max-w-2xl font-medium line-clamp-2">{p.description}</p>}
                        <div className="mt-3 inline-flex items-center gap-2 text-xs font-bold text-brand-yellow">
                            <Flag className="h-3.5 w-3.5" /> {p.completed_count}/{p.total_count} milestones complete
                        </div>
                    </div>
                    {/* progress ring */}
                    <div className="relative shrink-0">
                        <svg viewBox="0 0 72 72" className="w-16 h-16 sm:w-20 sm:h-20">
                            <circle cx="36" cy="36" r="30" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="7" />
                            <circle cx="36" cy="36" r="30" fill="none" stroke="#00DB79" strokeWidth="7" strokeLinecap="round"
                                strokeDasharray="188.5" transform="rotate(-90 36 36)"
                                style={{ strokeDashoffset: 188.5 - (188.5 * pct / 100), transition: 'stroke-dashoffset 0.8s ease' }} />
                            <text x="36" y="42" textAnchor="middle" fontSize="15" fontWeight="900" fill="#fff">{pct}%</text>
                        </svg>
                    </div>
                </div>
            </div>

            {/* Roadmap */}
            <div className="p-4 sm:p-6">
                {items.length === 0 && (
                    <div className="text-sm text-brand-navy/45 font-medium text-center py-4">No courses added to this path yet.</div>
                )}
                {items.map((it, i) => {
                    const st = STATUS[it.status] || STATUS.not_started;
                    const done = it.status === 'completed';
                    const active = it.status === 'in_progress';
                    const nextDone = i < items.length - 1 && items[i + 1] && (items[i].status === 'completed');
                    return (
                        <div key={it.course_id} className="relative flex gap-4">
                            {/* Spine + node */}
                            <div className="flex flex-col items-center">
                                <motion.div
                                    initial={{ scale: 0.6, opacity: 0 }} whileInView={{ scale: 1, opacity: 1 }} viewport={{ once: true }}
                                    transition={{ type: 'spring', stiffness: 240, damping: 16, delay: i * 0.05 }}
                                    className={`relative z-10 h-11 w-11 rounded-full flex items-center justify-center font-black shrink-0
                                        ${done ? 'bg-brand-yellow text-brand-black ring-4 ring-brand-yellow/20'
                                            : active ? 'bg-white text-brand-navy border-2 border-brand-yellow ring-4 ring-brand-yellow/20'
                                                : 'bg-white text-brand-navy/30 border-2 border-brand-navy/15'}`}>
                                    {done ? <Check className="h-5 w-5" strokeWidth={3} /> : active ? <Play className="h-4 w-4 fill-current ml-0.5" /> : <Lock className="h-4 w-4" />}
                                    {active && <span className="absolute inset-0 rounded-full border-2 border-brand-yellow animate-ping opacity-40" />}
                                </motion.div>
                                {i < items.length - 1 && (
                                    <div className="w-1 flex-1 my-1 rounded-full bg-brand-navy/10 overflow-hidden">
                                        <div className={`w-full ${nextDone ? 'h-full bg-brand-yellow' : 'h-0'} transition-all duration-700`} />
                                    </div>
                                )}
                            </div>

                            {/* Milestone card */}
                            <Link to={it.href}
                                className={`flex-1 mb-4 group rounded-2xl border p-4 flex items-center gap-3 transition-all duration-300 hover:-translate-y-0.5
                                    ${done ? 'border-emerald-200 bg-emerald-50/40' : active ? 'border-brand-yellow/40 bg-brand-yellow/[0.06] hover:shadow-[0_8px_24px_rgba(0,219,121,0.18)]' : 'border-brand-navy/5 bg-white hover:shadow-[0_8px_24px_rgba(35,31,32,0.08)]'}`}
                                data-testid={`pathway-course-${it.course_id}`}>
                                <div className="flex-1 min-w-0">
                                    <div className="text-[10px] font-bold uppercase tracking-wider text-brand-navy/45">Milestone {i + 1}{it.category ? ` · ${it.category_label || it.category}` : ''}</div>
                                    <div className="text-sm font-bold text-brand-black truncate mt-0.5">{it.title}</div>
                                    <div className={`text-[11px] font-bold mt-1 ${st.cls}`}>{st.label}{it.progress > 0 && it.status !== 'completed' ? ` · ${it.progress}%` : ''}</div>
                                </div>
                                <span className={`inline-flex items-center gap-1 text-xs font-bold px-3 py-1.5 rounded-lg shrink-0 ${done ? 'bg-emerald-500 text-white' : active ? 'bg-brand-yellow text-brand-black' : 'bg-brand-navy/5 text-brand-navy/60 group-hover:bg-brand-navy/10'}`}>
                                    {done ? 'Review' : active ? 'Continue' : 'Start'} <ChevronRight className="h-3.5 w-3.5" />
                                </span>
                            </Link>
                        </div>
                    );
                })}

                {/* Finish line */}
                {items.length > 0 && (
                    <div className="flex gap-4 items-center">
                        <div className={`h-11 w-11 rounded-full flex items-center justify-center shrink-0 ${allDone ? 'bg-brand-yellow text-brand-black ring-4 ring-brand-yellow/20' : 'bg-white text-brand-navy/30 border-2 border-brand-navy/15'}`}>
                            <Trophy className="h-5 w-5" />
                        </div>
                        <div className={`text-sm font-black ${allDone ? 'text-brand-black' : 'text-brand-navy/40'}`}>
                            {allDone ? '🎉 Path complete — well done!' : 'Finish line — complete all milestones'}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
